import { useEffect, useMemo, useRef, useState } from "react";
import { usePage } from "@inertiajs/react";
import { readsLocally, type LocalPage } from "@/lib/localRead";
import { structuralShare } from "@/lib/structuralShare";
import { dateInZone, type ViewSource } from "@/lib/views";
import { useTaskStore, viewTasks, type SyncList, type SyncTask } from "@/stores/taskStore";
import type { OutboxOp } from "@/lib/mutators/types";
import type { SharedProps } from "@/types";

interface TaskStoreRows {
  tasks: Record<string, SyncTask>;
  lists: Record<string, SyncList>;
}

const ROLLOVER_CHECK_MS = 60_000;
const NO_OPS: OutboxOp[] = [];

function deviceZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function todayIn(zone: string): string {
  const now = new Date().toISOString();
  try {
    return dateInZone(now, zone);
  } catch {
    return dateInZone(now, deviceZone());
  }
}

// The user's date now, not the one the last pull reported: a page left open overnight
// moves on to the new day by itself.
function useTodayIn(zone: string): string {
  const [today, setToday] = useState(() => todayIn(zone));

  useEffect(() => {
    const refresh = () => setToday(todayIn(zone));
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const timer = window.setInterval(refresh, ROLLOVER_CHECK_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [zone]);

  return today;
}

let lastSource: { tasks: TaskStoreRows["tasks"]; lists: TaskStoreRows["lists"]; source: ViewSource } | null = null;

// One source object per store state, so every mounted view reuses one preparation of the rows.
function sharedSource(tasks: TaskStoreRows["tasks"], lists: TaskStoreRows["lists"], today: string, zone: string): ViewSource {
  const last = lastSource;
  if (last && last.tasks === tasks && last.lists === lists && last.source.today === today && last.source.zone === zone) {
    return last.source;
  }
  const source = { tasks: Object.values(tasks), lists: Object.values(lists), today, zone };
  lastSource = { tasks, lists, source };
  return source;
}

/**
 * A view's result over the local store with the unconfirmed outbox ops replayed, or null while the page should read its props:
 * the page is not switched to local reads, the store is not ready, or it holds another
 * user's rows. `view` must be referentially stable (a module function or useCallback).
 * Parts of the result that did not change keep their identity between store updates.
 */
export function useLocalView<T>(page: LocalPage, view: (source: ViewSource) => T): T | null {
  const currentUser = usePage<SharedProps>().props.currentUser;
  const enabled = readsLocally(page);
  const active = useTaskStore(
    (s) => enabled && s.ready && !!currentUser && s.meta.userId === currentUser.id,
  );
  const tasks = useTaskStore((s) => (active ? s.tasks : null));
  const lists = useTaskStore((s) => (active ? s.lists : null));
  const pending = useTaskStore((s) => (active ? s.pending : NO_OPS));
  const storeZone = useTaskStore((s) => s.meta.zone);
  const zone = storeZone ?? currentUser?.timeZone ?? deviceZone();
  const today = useTodayIn(zone);

  const previous = useRef<T | null>(null);
  return useMemo(() => {
    if (!tasks || !lists) {
      previous.current = null;
      return null;
    }
    const rows = viewTasks({ tasks, lists, pending }, today);
    const result = structuralShare(previous.current, view(sharedSource(rows, lists, today, zone)));
    previous.current = result;
    return result;
  }, [view, tasks, lists, pending, today, zone]);
}
